import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { useState, useEffect, useRef } from 'react';
import { List, ListOrdered, AlignLeft, AlignRight, Undo2, Redo2, Link as LinkIcon, Unlink, Check, X } from 'lucide-react';

// Toolbar button component
const ToolbarButton = ({ onClick, isActive, children, title }) => (
    <button
        type="button"
        className={`tiptap-btn ${isActive ? 'active' : ''}`}
        onClick={onClick}
        onMouseDown={(e) => e.preventDefault()}
        title={title}
    >
        {children}
    </button>
);

// Link popup component
const LinkPopup = ({ editor, onClose }) => {
    const currentUrl = editor.getAttributes('link').href || '';
    const [url, setUrl] = useState(currentUrl || 'https://');
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    const handleApply = () => {
        const trimmed = url.trim();
        if (trimmed && trimmed !== 'https://') {
            editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
        }
        onClose();
    };

    const handleRemove = () => {
        editor.chain().focus().unsetLink().run();
        onClose();
    };

    return (
        <div className="tiptap-link-popup">
            <input
                ref={inputRef}
                className="tiptap-link-input"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleApply();
                    if (e.key === 'Escape') onClose();
                }}
                placeholder="https://ejemplo.com"
            />
            <button type="button" className="tiptap-link-btn apply" onClick={handleApply} title="Aplicar">
                <Check size={14} />
            </button>
            {currentUrl && (
                <button type="button" className="tiptap-link-btn remove" onClick={handleRemove} title="Quitar enlace">
                    <Unlink size={14} />
                </button>
            )}
            <button type="button" className="tiptap-link-btn cancel" onClick={onClose} title="Cancelar">
                <X size={14} />
            </button>
        </div>
    );
};

// Main TipTap Editor component
const TipTapEditor = ({ value, onChange, placeholder = 'Escribe aquí...' }) => {
    const [showLinkPopup, setShowLinkPopup] = useState(false);
    const [, forceUpdate] = useState(0);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3]
                }
            }),
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph']
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                }
            }),
            Placeholder.configure({
                placeholder
            })
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
        onSelectionUpdate: () => {
            forceUpdate(n => n + 1);
        }
    });

    // Sync external value changes
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value || '');
        }
    }, [value, editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="tiptap-editor">
            <div className="tiptap-toolbar">
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive('bold')}
                    title="Negrita"
                >
                    <strong>B</strong>
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive('italic')}
                    title="Cursiva"
                >
                    <em>I</em>
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    isActive={editor.isActive('underline')}
                    title="Subrayado"
                >
                    <u>U</u>
                </ToolbarButton>

                <span className="tiptap-divider" />

                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive('bulletList')}
                    title="Lista con viñetas"
                >
                    <List size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive('orderedList')}
                    title="Lista numerada"
                >
                    <ListOrdered size={16} />
                </ToolbarButton>

                <span className="tiptap-divider" />

                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    isActive={editor.isActive({ textAlign: 'left' })}
                    title="Alinear izquierda"
                >
                    <AlignLeft size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    isActive={editor.isActive({ textAlign: 'right' })}
                    title="Alinear derecha"
                >
                    <AlignRight size={16} />
                </ToolbarButton>

                <span className="tiptap-divider" />

                <ToolbarButton
                    onClick={() => setShowLinkPopup(!showLinkPopup)}
                    isActive={editor.isActive('link')}
                    title={editor.isActive('link') ? 'Editar enlace' : 'Agregar enlace'}
                >
                    <LinkIcon size={16} />
                </ToolbarButton>

                <span className="tiptap-divider" />

                <ToolbarButton
                    onClick={() => editor.chain().focus().undo().run()}
                    isActive={false}
                    title="Deshacer"
                >
                    <Undo2 size={16} />
                </ToolbarButton>
                <ToolbarButton
                    onClick={() => editor.chain().focus().redo().run()}
                    isActive={false}
                    title="Rehacer"
                >
                    <Redo2 size={16} />
                </ToolbarButton>
            </div>
            {showLinkPopup && (
                <LinkPopup editor={editor} onClose={() => setShowLinkPopup(false)} />
            )}
            <EditorContent editor={editor} className="tiptap-content" />
        </div>
    );
};

export default TipTapEditor;
